import * as React from 'react';
import {Suspense, useDeferredValue, useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Search} from 'lucide-react';
import {Link} from 'react-router-dom';
import {primaryButton} from './lib';
import {OpsInventoryPageQuery} from './__generated__/OpsInventoryPageQuery.graphql';

const QUERY = graphql`
    query OpsInventoryPageQuery($search: String, $category: EquipmentCategory) {
        assets(search: $search, category: $category, first: 200) {
            nodes {
                id
                assetTag
                name
                category
                status
                rentable
                serial
                kit { id assetTag }
                kitMembers { id }
            }
        }
    }`;

const CATEGORIES = [
    'KIT', 'CAMERA', 'LENS', 'CAMERA_ACCESSORY', 'LIGHTING', 'AUDIO_VIDEO', 'MEDIA', 'BATTERY', 'POWER', 'CABLE',
    'WORKSTATION', 'WORKSTATION_ACCESSORY', 'NETWORK', 'SECURITY', 'PRINTER', 'ETC',
] as const;

const statusClass: Record<string, string> = {
    AVAILABLE: 'text-emerald-400',
    IN_USE: 'text-lumina-accent',
    MAINTENANCE: 'text-amber-400',
    LOST: 'text-red-400',
    RETIRED: 'text-gray-500',
};

const InventoryTable: React.FC<{ search: string; category: string }> = ({search, category}) => {
    const data = useLazyLoadQuery<OpsInventoryPageQuery>(QUERY, {
        search: search || null,
        category: (category || null) as OpsInventoryPageQuery['variables']['category'],
    });
    const rows = data.assets.nodes;

    return (
        <>
            <p className="text-xs text-gray-500 mb-3">{rows.length} assets{rows.length === 200 ? ' (first 200)' : ''}</p>
            <div className="overflow-x-auto border border-gray-800">
                <table className="w-full text-sm">
                    <thead className="bg-black/50 text-left text-[11px] uppercase tracking-wider text-gray-500">
                    <tr>
                        <th className="px-3 py-2">Tag</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Category</th>
                        <th className="px-3 py-2">Serial</th>
                        <th className="px-3 py-2">Kit</th>
                        <th className="px-3 py-2">Status</th>
                        <th className="px-3 py-2">Rentable</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-900">
                    {rows.map((a) => (
                        <tr key={a.id} className="hover:bg-white/[0.03]">
                            <td className="px-3 py-2 font-mono text-xs text-gray-300 whitespace-nowrap">{a.assetTag}</td>
                            <td className="px-3 py-2 text-white">
                                <Link to={a.category === 'KIT' ? `/ops/kits/${encodeURIComponent(a.id)}` : `/ops/inventory/${encodeURIComponent(a.id)}`}
                                      className={a.category === 'KIT' ? 'text-lumina-accent hover:underline' : 'hover:text-lumina-accent hover:underline'}>
                                    {a.name}
                                </Link>
                                {a.kitMembers.length > 0 && (
                                    <span className="ml-2 text-[10px] text-gray-500">{a.kitMembers.length} pieces</span>
                                )}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-400">{a.category.replace(/_/g, ' ')}</td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-500">{a.serial ?? '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs text-gray-500">
                                {a.kit ? <Link to={`/ops/kits/${encodeURIComponent(a.kit.id)}`} className="hover:text-lumina-accent">{a.kit.assetTag}</Link> : '—'}
                            </td>
                            <td className={`px-3 py-2 text-xs font-bold ${statusClass[a.status] ?? ''}`}>{a.status}</td>
                            <td className="px-3 py-2 text-xs">{a.rentable ? 'Yes' : <span className="text-gray-600">No</span>}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
};

const OpsInventoryPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const deferredSearch = useDeferredValue(search);

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <h1 className="text-3xl font-display font-bold">Inventory</h1>
                <div className="flex gap-3">
                    <Link to="/ops/inventory/new" className={primaryButton}>New item</Link>
                    <Link to="/ops/kits/new" className={primaryButton}>New kit</Link>
                    <label className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Scan or search tag, make, model, serial…"
                            autoFocus
                            className="bg-black border border-gray-800 pl-9 pr-3 py-2 text-sm w-80 focus:border-lumina-purple outline-none"
                        />
                    </label>
                    <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="bg-black border border-gray-800 px-3 py-2 text-sm focus:border-lumina-purple outline-none"
                    >
                        <option value="">All categories</option>
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
                        ))}
                    </select>
                </div>
            </div>
            <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                <InventoryTable search={deferredSearch} category={category}/>
            </Suspense>
        </div>
    );
};

export default OpsInventoryPage;
